export interface VoiceClip {
  blob: Blob;
  mime: string;
  durationMs: number;
  url: string;
}

const PREFERRED = ['audio/ogg;codecs=opus', 'audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];

export function voiceSupported(): boolean {
  return typeof window !== 'undefined' && !!navigator.mediaDevices?.getUserMedia && typeof (window as any).MediaRecorder !== 'undefined';
}

/**
 * Запись голоса в файл (MediaRecorder). Файл уходит на сервер,
 * где транскрибируется (SpeechKit / Whisper) и превращается в текст сообщения.
 */
export class VoiceRecorder {
  private rec: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private stream: MediaStream | null = null;
  private started = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  onLevel?: (level: number) => void;
  private raf = 0;

  constructor(private maxMs = 5 * 60_000) {}

  get recording() {
    return !!this.rec && this.rec.state === 'recording';
  }

  get elapsedMs() {
    return this.recording ? Date.now() - this.started : 0;
  }

  async start(onAutoStop?: (clip: VoiceClip) => void): Promise<void> {
    this.stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
    const MR = (window as any).MediaRecorder as typeof MediaRecorder;
    const mime = PREFERRED.find((m) => MR.isTypeSupported?.(m)) ?? '';
    this.rec = new MR(this.stream, mime ? { mimeType: mime, audioBitsPerSecond: 32000 } : undefined);
    this.chunks = [];
    this.rec.ondataavailable = (e) => e.data.size && this.chunks.push(e.data);
    this.rec.start(1000);
    this.started = Date.now();
    this.meter();
    this.timer = setTimeout(async () => {
      const clip = await this.stop();
      if (clip) onAutoStop?.(clip);
    }, this.maxMs);
  }

  private meter() {
    if (!this.stream || !this.onLevel) return;
    try {
      const ctx = new AudioContext();
      const src = ctx.createMediaStreamSource(this.stream);
      const an = ctx.createAnalyser();
      an.fftSize = 256;
      src.connect(an);
      const data = new Uint8Array(an.frequencyBinCount);
      const tick = () => {
        if (!this.recording) {
          ctx.close();
          return;
        }
        an.getByteTimeDomainData(data);
        let peak = 0;
        for (const v of data) peak = Math.max(peak, Math.abs(v - 128));
        this.onLevel?.(peak / 128);
        this.raf = requestAnimationFrame(tick);
      };
      tick();
    } catch {
      /* визуализация необязательна */
    }
  }

  async stop(): Promise<VoiceClip | null> {
    const rec = this.rec;
    if (!rec) return null;
    if (this.timer) clearTimeout(this.timer);
    cancelAnimationFrame(this.raf);
    const done = new Promise<void>((r) => (rec.onstop = () => r()));
    if (rec.state !== 'inactive') rec.stop();
    await done;
    this.stream?.getTracks().forEach((t) => t.stop());
    const mime = (rec.mimeType || this.chunks[0]?.type || 'audio/webm').split(';')[0];
    const blob = new Blob(this.chunks, { type: mime });
    const durationMs = Date.now() - this.started;
    this.rec = null;
    this.stream = null;
    if (!blob.size) return null;
    return { blob, mime, durationMs, url: URL.createObjectURL(blob) };
  }

  cancel() {
    if (this.timer) clearTimeout(this.timer);
    try {
      this.rec?.stop();
    } catch {
      /* noop */
    }
    this.stream?.getTracks().forEach((t) => t.stop());
    this.rec = null;
    this.stream = null;
  }
}
